import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Input, Button, Space, Row, Col, Select } from 'antd';
import SelectWithCreate, { normalizeText } from './SelectWithCreate';

const EditPersonaModal = ({
  visible,
  onCancel,
  onSubmit,
  initialValues,
  paises = [],
  ciudades = [],
  setCiudades,
  saving = false,
  title = 'Editar Persona'
}) => {
  const [form] = Form.useForm();
  const [ciudadesFiltradas, setCiudadesFiltradas] = useState([]);
  const paisId = Form.useWatch('PaisId', form);

  const paisOptions = useMemo(() =>
    paises
      .map(p => ({ value: p.PaisId, label: p.PaisNombre, flagUrl: p.PaisBanderaUrl }))
      .filter(p => p.label) // Filtrar elementos sin label
      .sort((a, b) => (a.label || '').localeCompare(b.label || '')),
    [paises]
  );

  const ciudadOptions = useMemo(() =>
    ciudadesFiltradas
      .map(c => ({ value: c.CiudadId, label: c.CiudadNombre }))
      .filter(c => c.label) // Filtrar elementos sin label
      .sort((a, b) => (a.label || '').localeCompare(b.label || '')),
    [ciudadesFiltradas]
  );

  useEffect(() => {
    if (paisId) {
      const filtered = ciudades.filter(c => c.PaisId === paisId);
      setCiudadesFiltradas(filtered);
    } else {
      setCiudadesFiltradas([]);
    }
  }, [paisId, ciudades]);

  useEffect(() => {
    if (visible && initialValues) {
      // Si hay un PaisId en los initialValues, filtrar ciudades primero
      if (initialValues.PaisId) {
        const filtered = ciudades.filter(c => c.PaisId === initialValues.PaisId);
        setCiudadesFiltradas(filtered);
      }

      // Usar setTimeout para asegurar que el form esté montado
      setTimeout(() => {
        form.setFieldsValue({
          PersonaNombre: initialValues.PersonaNombre || '',
          PersonaApellido: initialValues.PersonaApellido || '',
          PersonaNombreCompleto: initialValues.PersonaNombreCompleto || '',
          PersonaApodo: initialValues.PersonaApodo || '',
          PaisId: initialValues.PaisId || null,
          CiudadId: initialValues.CiudadId || null,
          PersonaFecNac: initialValues.PersonaFecNac ? String(initialValues.PersonaFecNac).substring(0, 10) : '',
          PersonaFecDef: initialValues.PersonaFecDef ? String(initialValues.PersonaFecDef).substring(0, 10) : '',
        });
      }, 0);
    } else if (visible && !initialValues) {
      form.resetFields();
      setCiudadesFiltradas([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, initialValues, form]);

  const handleSubmit = async (values) => {
    const payload = {
      ...values,
      PersonaFecNac: values.PersonaFecNac || null,
      PersonaFecDef: values.PersonaFecDef || null,
    };
    await onSubmit(payload);
    form.resetFields();
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={500}
      destroyOnClose
      zIndex={1100}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: 16 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="PersonaNombre" label="Nombre">
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="PersonaApellido" label="Apellido" rules={[{ required: true, message: 'Requerido' }]}>
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="PersonaNombreCompleto" label="Nombre Completo">
          <Input />
        </Form.Item>
        <Form.Item name="PersonaApodo" label="Apodo">
          <Input />
        </Form.Item>
        <Form.Item name="PaisId" label="País" rules={[{ required: true, message: 'Requerido' }]}>
          <Select
            options={paisOptions}
            showSearch
            allowClear
            filterOption={(input, opt) => normalizeText(opt?.label ?? '').includes(normalizeText(input))}
            onChange={() => form.setFieldValue('CiudadId', null)}
            optionRender={(option) => {
              const flagUrl = option.data?.flagUrl ?? option.flagUrl;
              return (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {flagUrl && <img src={flagUrl} alt="" style={{ width: 20, height: 14, objectFit: 'cover', borderRadius: 2 }} />}
                  <span>{option.label}</span>
                </span>
              );
            }}
            labelRender={(props) => {
              const opt = paisOptions.find(o => o.value === props.value);
              return (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {opt?.flagUrl && <img src={opt.flagUrl} alt="" style={{ width: 20, height: 14, objectFit: 'cover', borderRadius: 2 }} />}
                  <span>{props.label}</span>
                </span>
              );
            }}
          />
        </Form.Item>
        <Form.Item name="CiudadId" label="Ciudad">
          <SelectWithCreate
            options={ciudadOptions}
            createLabel="Crear Ciudad"
            createFields={[{ name: 'CiudadNombre', label: 'Nombre', required: true }]}
            onCreateSubmit={async (vals) => {
              const services = await import('../services/api').then(m => m.services);
              const payload = { ...vals, PaisId: paisId };
              const res = await services.ciudades.create(payload);
              const c = res.data;
              const ciudad = {
                CiudadId: c.ciudadid || c.CiudadId || c.id,
                CiudadNombre: c.ciudadnombre || c.CiudadNombre || c.nombre || vals.CiudadNombre,
                PaisId: c.paisid || c.PaisId || paisId
              };
              if (setCiudades) setCiudades(prev => [...prev, ciudad]);
              setCiudadesFiltradas(prev => [...prev, ciudad]);
              return { value: ciudad.CiudadId, label: ciudad.CiudadNombre };
            }}
            showSearch
            allowClear
            disabled={!paisId}
            placeholder={paisId ? 'Seleccionar ciudad' : 'Primero seleccione un país'}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="PersonaFecNac" label="Fecha de Nacimiento">
              <Input placeholder="YYYY-MM-DD" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="PersonaFecDef" label="Fecha de Defunción">
              <Input placeholder="YYYY-MM-DD" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Space>
            <Button onClick={handleCancel}>Cancelar</Button>
            <Button type="primary" htmlType="submit" loading={saving}>Guardar</Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditPersonaModal;
